/**
 * NPC-definisjoner (fase 3 + 5). Alle karakterer er fiktive bransje-
 * arketyper — satiren treffer systemet (portvokterkultur, gamle
 * forlagshus), aldri virkelige personer. Timeplanene bor i
 * data/schedules.ts, dialog-/hendelsesdata i data/events.ts og
 * data/rivalDialog.ts.
 */

export interface NpcDef {
  id: string;
  navn: string;
  tittel: string;
  /** Tekstur-nøkkel for portrett når asset er prosessert; placeholder ellers. */
  portrettKey?: string;
  /** Kort replikk ved første møte. */
  hilsen: string;
  /** Hvilket forlag karakteren tilhører. */
  forlag?: 'spiller' | 'kapelledamm';
  /** Rekrutterbar forfatter (5D): profil-id i GameState-registeret. */
  forfatterProfilId?: string;
}

export const NPCER: NpcDef[] = [
  {
    id: 'agent-thomas',
    navn: 'Agent Thomas',
    tittel: 'Litterær agent',
    portrettKey: 'npc-agent-thomas',
    hilsen: '«Jeg har tre manus du MÅ se på. To av dem er til og med gode.»',
  },
  {
    id: 'kristine-mentor',
    navn: 'Kristine',
    tittel: 'Mentor, Forfatterskolen',
    portrettKey: 'npc-kristine-mentor',
    hilsen: '«Sven! Husk: det er lov å si nei til dårlige manus. Det er jobben.»',
  },
  {
    id: 'kritiker-aftenposten',
    navn: 'Kritikeren fra Aftenposten',
    tittel: 'Hovedanmelder',
    portrettKey: 'npc-kritiker-aftenposten',
    hilsen: '«Jeg lover ingenting. Men jeg leser alt dere sender. Dessverre.»',
  },
  // ---- Kapelle Damm (fase 5) ----
  {
    id: 'kapelle-rival',
    navn: 'Preben Wahl-Schreiner',
    tittel: 'Forlagsredaktør, Kapelle Damm',
    portrettKey: 'npc-kapelle-rival',
    forlag: 'kapelledamm',
    hilsen:
      '«Å, du er den nye i Forlagshuset? Så friskt. Vi i de etablerte forlagene heier alltid på entusiasme.»',
  },
  {
    id: 'kapelle-resepsjonist',
    navn: 'Resepsjonisten',
    tittel: 'Kapelle Damm',
    portrettKey: 'npc-kapelle-resepsjonist',
    forlag: 'kapelledamm',
    hilsen: '«Velkommen til Kapelle Damm. Har du en avtale?»',
  },
  // ---- Kapelle Damms stall (fase 5D) — fiktive parodi-forfattere ----
  {
    id: 'kapelle-lyriker',
    navn: 'Aslak Wendelbo',
    tittel: 'Lyriker (kritikerrost)',
    portrettKey: 'npc-kapelle-lyriker',
    forlag: 'kapelledamm',
    forfatterProfilId: 'aslak_wendelbo',
    hilsen:
      '«Min siste samling solgte 214 eksemplarer. Morgenbladet kalte den ‘uomgjengelig’. Ingen av delene betaler husleia.»',
  },
  {
    id: 'kapelle-krimmaskin',
    navn: 'Bodil Krantz',
    tittel: 'Krimforfatter (bok 14 i serien)',
    portrettKey: 'npc-kapelle-krimmaskin',
    forlag: 'kapelledamm',
    forfatterProfilId: 'bodil_krantz',
    hilsen:
      '«Ikke spør om boka. Det er den samme som de tretten forrige, bare med en annen fjord.»',
  },
  {
    id: 'kapelle-misfornoyd',
    navn: 'Jonas Hovland',
    tittel: 'Romanforfatter',
    portrettKey: 'npc-kapelle-misfornoyd',
    forlag: 'kapelledamm',
    forfatterProfilId: 'jonas_hovland',
    hilsen:
      '«Hos Kapelle Damm får jeg svar på e-post hvert kvartal. Hvis jeg purrer.»',
  },
];

export function finnNpc(id: string): NpcDef | undefined {
  return NPCER.find((n) => n.id === id);
}

/**
 * Resepsjonistens kommentarer ved gjentatte besøk på KD-etasjen (5A):
 * nysgjerrig først, gradvis mer spiss. Indeks = min(besøk-1, siste).
 */
export const KAPELLE_RESEPSJONIST_KOMMENTARER: string[] = [
  '«Velkommen til Kapelle Damm. Du er… ny i bygget? Spennende.»',
  '«Tilbake igjen? Jeg skal hilse redaktøren. Kanskje.»',
  '«Du vet at vi ikke deler ut katalogen vår, sant?»',
  '«Skal jeg melde deg på omvisning, eller?»',
];

export function kapelleResepsjonistKommentar(besokNr: number): string {
  const i = Math.max(0, Math.min(besokNr - 1, KAPELLE_RESEPSJONIST_KOMMENTARER.length - 1));
  return KAPELLE_RESEPSJONIST_KOMMENTARER[i];
}
